import { registerFontFace } from '@sas-dvr/internal-va-react-core/components/FontProvider/loadFonts';
import { fetchFontDefinitions, FontDefinition } from './FontService';

export async function registerServerFonts(url: string) {
  const fontDefinitions = await fetchFontDefinitions(url);
  const groupedDefinitions: Record<string, FontDefinition[]> = {};
  for (const font of fontDefinitions) {
    const key = getFontKey(font);
    groupedDefinitions[key] = groupedDefinitions[key] ?? [];
    groupedDefinitions[key].push(font);
  }
  for (const group of Object.values(groupedDefinitions)) {
    const fontFace = createFontFace(url, group);
    if (fontFace !== null) {
      registerFontFace(fontFace);
    }
  }
}

/**
 * Returns a string describing the FontFace. Used to deduplicate similar font definitions.
 */
function getFontKey({ family, weight, style }: FontDefinition | FontFace): string {
  return `${style} ${weight} "${family}"`;
}

/**
 * Creates a FontFace given a list of FontDefinitions and the server they were obtained from.
 * The first definition is used to set the font metadata.
 */
function createFontFace(serverUrl: string, fontDefinitions: FontDefinition[]): FontFace | null {
  const fontDefinition = fontDefinitions[0];
  if (!fontDefinition?.family) {
    return null;
  }
  const opts: FontFaceDescriptors = { display: 'swap' };
  if (fontDefinition.usWeightClass) {
    opts.weight = '' + fontDefinition.usWeightClass;
  }
  if (fontDefinition.weight) {
    opts.weight = fontDefinition.weight;
  }
  if (fontDefinition.style) {
    opts.style = fontDefinition.style;
  }
  const localSources = [];
  const serverSources = [];
  if (fontDefinition.name) {
    localSources.push(`local('${fontDefinition.name}')`);
  }
  if (fontDefinition.postScriptName && fontDefinition.postScriptName !== fontDefinition.name) {
    localSources.push(`local('${fontDefinition.postScriptName}')`);
  }
  const contentUris: { [F in Required<FontDefinition>['format']]?: string } = {};
  for (const fd of fontDefinitions) {
    const contentUri = fd.links?.find((link) => link.rel === 'content')?.uri;
    if (fd.format && contentUri) {
      contentUris[fd.format] = contentUri;
    }
  }
  // Prefer woff over ttf. ttc is not used due to browser compatibility issues.
  if (contentUris.woff) {
    serverSources.push(`url('${serverUrl}${contentUris.woff}') format('woff')`);
  }
  if (contentUris.ttf) {
    serverSources.push(`url('${serverUrl}${contentUris.ttf}') format('truetype')`);
  }
  if (!serverSources.length) {
    return null;
  }
  return new FontFace(fontDefinition.family, [...localSources, ...serverSources].join(', '), opts);
}
